import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { Loader } from "../Loader/Loader";
import Search from "../Components/Search"; 
import Pagination, { usePagination } from "../Components/Pagination";
import { decryptData } from "../../utils/storageCrypto";
const InvoicesList = () => {
  const navigate = useNavigate();
  const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET;

  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [searchChips, setSearchChips] = useState(() => {
    const saved = sessionStorage.getItem('invoiceSearchChips');
    return saved ? JSON.parse(saved) : [];
  });

  const searchTypes = ["Invoice No", "Order No", "Customer Name", "Status"];

  // Pagination
  const {
    currentItems,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
    resetToFirstPage,
  } = usePagination(filteredInvoices, 10);

  const getStatusColor = (status) => {
    switch (status) {
      case "Paid":
        return "text-green-500";
      case "Pending":
        return "text-gray-500";
      case "Overdue":
        return "text-red-500";
      default:
        return "text-gray-700";
    }
  };

  const getAllInvoices = async () => {
        const encryptedToken = sessionStorage.getItem('token');
            const token = decryptData(encryptedToken);
            const encryptedOrganizationId = sessionStorage.getItem('organizationId');
            const organizationId = decryptData(encryptedOrganizationId);
    try {
      const response = await fetch(`${fetchUrl}invoice/get-all-invoices`, {
        method: "GET",
        headers: { "Content-Type": "application/json",
          'x-token':token
         },
      });
      const data = await response.json();
      if (data.success) {
        setInvoices(data.data);
        setFilteredInvoices(data.data);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllInvoices();
  }, []);

  // Search filter (type-specific, like DeliveryChallanList)
  const handleSearch = (query, type) => {
  // ✅ If no search text, do NOT filter
  if (!query || query.trim() === "") {
    setFilteredInvoices(invoices);
    resetToFirstPage();
    return;
  }

  // ✅ If search type not selected, do nothing
  if (!type) return;

  const lowercaseQuery = query.toLowerCase();

  const filtered = invoices.filter((invoice) => {
    switch (type) {
      case "Invoice No":
        return invoice?.invoice_number?.toLowerCase().includes(lowercaseQuery);
      case "Order No":
        return invoice?.order_number?.toLowerCase().includes(lowercaseQuery);
      case "Customer Name":
        return invoice?.customer_id?.customer_name
          ?.toLowerCase()
          .includes(lowercaseQuery);
      case "Status":
        return invoice?.status?.toLowerCase().includes(lowercaseQuery);
      default:
        return false;
    }
  });

  setFilteredInvoices(filtered);
  resetToFirstPage();
};


  const handleRemoveSearchChip = (chipToRemove) => {
    const updated = searchChips.filter(
      (chip) => !(chip.query === chipToRemove.query && chip.type === chipToRemove.type)
    );
    setSearchChips(updated);
    sessionStorage.setItem('invoiceSearchChips', JSON.stringify(updated));

    if (updated.length === 0) {
      setFilteredInvoices(invoices);
      setSearchQuery('');
      setSearchType(''); 
    } else {
      const last = updated[updated.length - 1];
      handleSearch(last.query, last.type);
    }
  };

  const handleAddSearchChip = (query, type) => {
    if (!type) return;

    const actualQuery = query.trim(); 

    setSearchChips((prev) => {
      const exists = prev.some((chip) => chip.query === actualQuery && chip.type === type);
      if (exists) return prev;

      const updated = [...prev, { query: actualQuery, type }]; 
      sessionStorage.setItem('invoiceSearchChips', JSON.stringify(updated)); 
      return updated;
    });

    setSearchQuery(actualQuery); 
    setSearchType(type);        

    handleSearch(actualQuery, type);
  };

  const handleChipClick = (chip) => {
    setSearchType(chip.type);
    setSearchQuery(chip.query);
    handleSearch(chip.query, chip.type);
  };

  // Sorting (updated to handle potential nested keys, like DeliveryChallanList)
  const sortArray = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredInvoices].sort((a, b) => {
      const aValue = key.split('.').reduce((obj, k) => obj && obj[k], a) || '';
      const bValue = key.split('.').reduce((obj, k) => obj && obj[k], b) || '';
      
      const aString = aValue.toString().toLowerCase();
      const bString = bValue.toString().toLowerCase();

      if (aString < bString) return direction === "ascending" ? -1 : 1;
      if (aString > bString) return direction === "ascending" ? 1 : -1;
      return 0;
    });

    setFilteredInvoices(sorted);
    resetToFirstPage();
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? faSortUp : faSortDown;
    }
    return null;
  };

  const handleCreate = () => navigate("/bb/app/sales/newinvoice");
  const handleNavigateToInvoiceDetails = (invoice) =>
    navigate("/bb/app/sales/invoicedetails", {
      state: { InvoiceData: invoice, invoiceId: invoice._id },
    });

  if (isLoading) return <Loader />;
  

  return (
    <div className="bg-white flex flex-row w-full justify-left h-full overflow-y-hidden">
      <div className="flex flex-row w-full">
        <div className="w-full flex-1">
          <div className="flex flex-col w-full h-full justify-center items-center">
            <div className="w-full h-full flex flex-col justify-between">
              {/* Header (matching DeliveryChallanList layout) */}
              <div className="w-full h-16 border-b flex items-center">
                <div className="flex w-full justify-between px-5 items-center">
                  <div className='space-x-10 flex flex-row mb-4 mt-4'>
                    <h1 className="text-lg font-bold whitespace-nowrap">Invoices</h1>
                    <Search
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      searchType={searchType}
                      setSearchType={setSearchType}
                      searchTypes={searchTypes}
                      onSearch={handleSearch}
                      onSearchSubmit={handleAddSearchChip}
                      placeholder="Search..."
                    />
                    {searchChips.length > 0 && (
                      <div className="w-full flex flex-cols gap-3 items-center mt-2 px-4">
                        {searchChips.map((chip, index) => {
                          const isActive = chip.type === searchType;

                          const chipClasses = `flex items-center max-w-xs text-sm px-3 py-1 rounded-full shadow-sm transition-all duration-150 ${
                            isActive
                              ? 'bg-red-500 text-white'
                              : 'bg-gray-100 text-gray-800'
                          }`;

                          return (
                            <div
                              key={`${chip.type}-${index}`}
                              className={chipClasses}
                              style={{ whiteSpace: 'nowrap' }}
                              onClick={() => handleChipClick(chip)}
                            >
                              <span className="whitespace-nowrap max-w-[130px]">
                                {chip.query ? chip.query : chip.type}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveSearchChip(chip);
                                }}
                                className={`ml-2 text-base font-bold ${
                                  isActive ? 'text-white hover:text-gray-200' : 'text-gray-500 hover:text-red-500'
                                }`}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* New Invoice Button */}
                  <button
                    onClick={handleCreate}
                    className="bg-[#F7525A] text-white z-20 px-3 py-1.5 rounded hover:bg-[#d14148]"
                     title="" data-tooltip="Create Invoice"  style={{ '--tooltip-bg': 'black' }}
                  >
                    + New
                  </button>
                </div>
              </div>

              {/* Pagination Info (top) */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={10}
                showInfo={true}
                hideControls={true}
                itemName="invoices"
              />

              {/* Table */}
           <div className="w-full h-full bg-white rounded-xs overflow-hidden">
  <div className="overflow-y-auto h-[80vh]">
    {invoices.length === 0 ? (
      <div className="m-4 text-center text-gray-500">
        <div className="p-4 border border-gray-300 rounded-md">
          <h1 className="text-md ">No Invoices Available</h1>
        </div>

        <div className="mt-6 flex flex-col items-center">
          <h1 className="text-xl font-bold mb-3 text-gray-800">
            Create New Invoice
          </h1>
          <button
            className="bg-[#F7525A] text-white px-2 py-1 rounded-md font-medium hover:bg-[#e3444d] transition"
            onClick={handleCreate}
          >
            Create
          </button>
        </div>
      </div>
                  ) : (
                    <table className="w-full mb-5">
                      <thead className="sticky top-0 z-10 bg-[#fafafc] text-[#6c7184] border-b">
                        <tr className="border-b">
                          <th className="text-xs py-3 text-center">DATE</th>
                          <th
                            className="text-xs py-3 text-center cursor-pointer"
                            onClick={() => sortArray("invoice_number")}
                          >
                            INVOICE No{" "}
                            {getSortIcon("invoice_number") && (
                              <FontAwesomeIcon icon={getSortIcon("invoice_number")} />
                            )}
                          </th>
                          <th className="text-xs py-3 text-center">ORDER No</th>
                          <th className="text-xs py-3 text-center">CUSTOMER NAME</th>
                          <th className="text-xs py-3 text-center">STATUS</th>
                          <th className="text-xs py-3 text-center">DUE DATE</th>
                          <th className="text-xs py-3 text-center">AMOUNT</th>
                          <th className="text-xs py-3 text-center">BALANCE DUE</th>
                        </tr>
                      </thead>

                      <tbody className="border-b border-t">
                        {currentItems.length > 0 ? (
                          currentItems.map((invoice) => (
                            <tr
                              key={invoice._id}
                              className="text-sm border-b hover:bg-gray-50"
                            >
                              <td className="py-2 text-center">
                                {new Date(invoice?.invoice_date).toLocaleDateString()}
                              </td>
                              <td
                                className="py-2 text-center text-[#408dfb] font-500 cursor-pointer"
                                onClick={() => handleNavigateToInvoiceDetails(invoice)}
                              >
                                {invoice?.invoice_number}
                              </td>
                              <td className="py-2 text-center">{invoice?.order_number}</td>
                              <td className="py-2 text-center">
                                {invoice?.customer_id?.customer_name}
                              </td>
                              <td
                                className={`py-2 text-center ${getStatusColor(
                                  invoice?.status
                                )}`}
                              >
                                {invoice?.status}
                              </td>
                              <td className="py-2 text-center">
                                {new Date(invoice?.due_date).toLocaleDateString()}
                              </td>
                              <td className="py-2 text-center">
                                ₹
                                {invoice?.total?.toLocaleString("hi-IN", {
                                  minimumFractionDigits: 2,
                                })}
                              </td>
                              <td className="py-2 text-center">
                                ₹
                                {invoice?.balance_due?.toLocaleString("hi-IN", {
                                  minimumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          ))
                        ) : (
                          // No search results
                          <tr>
                            <td colSpan="8" className="p-12 text-center text-gray-500">
                              <div className="flex flex-col items-center">
                                <FontAwesomeIcon
                                  icon={faSearch}
                                  className="text-4xl text-gray-300 mb-4"
                                />
                                <p className="text-lg font-medium mb-2">
                                  No Result Found.
                                </p>
                                <p className="text-sm">
                                  Try adjusting your search criteria.
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Pagination Bottom */}
              <div className="sticky bottom-0 bg-white shadow-md z-10 px-4 py-2">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={10}
                  onPageChange={handlePageChange}
                  onPreviousPage={handlePreviousPage}
                  onNextPage={handleNextPage}
                  showInfo={false}
                  itemName="invoices"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicesList;
